import GeminiProvider from '../providers/GeminiProvider';
import OpenAIProvider from '../providers/OpenAIProvider';
import OpenRouterProvider from '../providers/OpenRouterProvider';
import { getProviderConfig, getEnabledProviders } from '../config/aiConfig';
import { supabase } from '../../../services/supabaseClient';

/**
 * AI Provider Factory
 * Creates and manages AI provider instances with fallback support
 */
class AIProviderFactory {
  constructor() {
    this.providers = new Map();
    this.healthStatus = new Map();
    this.lastHealthCheck = new Map();
    this.HEALTH_CHECK_INTERVAL = 60000; // 1 minute
    this.dbConfigs = null;
    this.lastDbLoad = 0;
    this.DB_CACHE_TTL = 30000; // 30 seconds
  }

  /**
   * Load provider configurations from database
   */
  async loadDbConfigs() {
    const now = Date.now();
    if (this.dbConfigs && (now - this.lastDbLoad) < this.DB_CACHE_TTL) {
      return this.dbConfigs;
    }

    try {
      console.log('[AI Factory] Loading provider configs from database');
      const { data, error } = await supabase
        .from('ai_provider_config')
        .select('*')
        .order('priority', { ascending: true });

      if (error) {
        console.warn('[AI Factory] Error loading DB configs:', error);
        return null;
      }

      this.dbConfigs = data;
      this.lastDbLoad = now;
      console.log('[AI Factory] Loaded', data?.length || 0, 'provider configs from database');
      return data;
    } catch (error) {
      console.error('[AI Factory] Failed to load DB configs:', error);
      return null;
    }
  }

  /**
   * Get provider configuration, checking database first
   */
  async getProviderConfigFromDb(providerName) {
    const dbConfigs = await this.loadDbConfigs();
    if (!dbConfigs) {
      return getProviderConfig(providerName);
    }

    const dbConfig = dbConfigs.find(c => c.provider_name === providerName);
    if (!dbConfig) {
      return getProviderConfig(providerName);
    }

    // Merge database config with default config
    // IMPORTANT: API keys from .env ALWAYS take priority over database-stored keys
    const defaultConfig = getProviderConfig(providerName) || {};
    const mergedConfig = {
      ...defaultConfig,
      ...dbConfig,
      // Map database fields to expected fields
      name: dbConfig.provider_name,
      displayName: dbConfig.display_name,
      model: dbConfig.model_name,
      enabled: dbConfig.enabled,
      priority: dbConfig.priority,
      // Configuration from JSONB fields
      ...dbConfig.configuration,
      capabilities: dbConfig.capabilities,
      pricing: dbConfig.pricing,
      rateLimits: dbConfig.rate_limits,
      // CRITICAL: Always prefer .env/code config over database-stored config for Critical Fields
      apiKey: defaultConfig.apiKey || dbConfig.configuration?.apiKey,
      model: defaultConfig.model || dbConfig.model_name || dbConfig.configuration?.model
    };

    console.log(`[AI Factory] Using DB config for ${providerName}:`, {
      model: mergedConfig.model,
      enabled: mergedConfig.enabled,
      priority: mergedConfig.priority
    });

    return mergedConfig;
  }

  /**
   * Get enabled providers from database or defaults
   */
  async getEnabledProvidersFromDb() {
    console.log('[AI Factory] Loading enabled providers from database...');

    const dbConfigs = await this.loadDbConfigs();
    console.log('[AI Factory] Loaded DB configs:', dbConfigs?.length || 0);

    if (!dbConfigs || dbConfigs.length === 0) {
      console.log('[AI Factory] No DB configs, using defaults');
      return getEnabledProviders();
    }

    // Filter enabled providers from database
    const enabledDbConfigs = dbConfigs.filter(c => c.enabled);
    console.log('[AI Factory] Filtered enabled configs:', enabledDbConfigs.length);

    enabledDbConfigs.sort((a, b) => (a.priority || 999) - (b.priority || 999));

    // Map to expected format
    const result = enabledDbConfigs.map(config => ({
      name: config.provider_name,
      displayName: config.display_name,
      model: config.model_name,
      enabled: config.enabled,
      priority: config.priority,
      baseUrl: config.base_url,
      apiKey: getProviderConfig(config.provider_name)?.apiKey,
      capabilities: config.capabilities,
      pricing: config.pricing,
      rateLimits: config.rate_limits
    }));

    console.log('[AI Factory] Mapped providers:', result.map(p => `${p.name}:${p.model}`));

    return result;
  }

  /**
   * Get provider instance by name
   * @param {string} providerName - Provider name (gemini, openai, anthropic, etc.)
   * @param {Object} customConfig - Optional custom configuration
   * @returns {BaseAIProvider} - Provider instance
   */
  async getProvider(providerName, customConfig = null) {
    // Check if provider is already instantiated
    if (this.providers.has(providerName)) {
      return this.providers.get(providerName);
    }

    // Get provider configuration from database first
    const config = customConfig || await this.getProviderConfigFromDb(providerName);

    console.log(`[AI Factory] Config for ${providerName}:`, {
      hasConfig: !!config,
      enabled: config?.enabled,
      model: config?.model,
      hasApiKey: !!config?.apiKey
    });

    if (!config) {
      throw new Error(`Provider "${providerName}" is not configured`);
    }

    if (!config.enabled) {
      throw new Error(`Provider "${providerName}" is disabled`);
    }

    if (!config.apiKey) {
      throw new Error(`Provider "${providerName}" is missing API key`);
    }

    // Create provider instance based on name
    let provider;

    switch (providerName.toLowerCase()) {
      case 'gemini':
        provider = new GeminiProvider(config);
        break;

      case 'openai':
        provider = new OpenAIProvider(config);
        break;

      case 'azure':
        // Azure OpenAI uses OpenAI SDK with custom endpoint
        provider = new OpenAIProvider({
          ...config,
          baseUrl: `${config.baseUrl}/openai/deployments/${config.model}`,
          defaultQuery: { 'api-version': config.apiVersion }
        });
        break;

      case 'openrouter':
        // OpenRouter aggregates 200+ models
        provider = new OpenRouterProvider(config);
        break;

      case 'anthropic':
        // Anthropic provider would go here
        throw new Error('Anthropic provider not yet implemented');

      default:
        // For dynamic providers from Supabase, use appropriate provider based on config
        const providerType = config.configuration?.providerType;
        if (providerType === 'openrouter') {
          provider = new OpenRouterProvider(config);
        } else if (providerType === 'openai' || !providerType) {
          // Default to OpenAI-compatible for unknown providers
          provider = new OpenAIProvider(config);
        } else if (providerType === 'gemini') {
          provider = new GeminiProvider(config);
        } else {
          throw new Error(`Unknown provider: ${providerName} with type: ${providerType}`);
        }
    }

    // Cache the provider instance
    this.providers.set(providerName, provider);

    return provider;
  }

  /**
   * Get provider with automatic fallback
   * Tries primary provider, falls back to alternatives if it fails
   * @param {string} primaryProviderName - Primary provider to try
   * @param {Array<string>} fallbackProviders - Fallback providers in order
   * @returns {BaseAIProvider} - Working provider instance
   */
  async getProviderWithFallback(primaryProviderName, fallbackProviders = []) {
    const providersToTry = [primaryProviderName, ...fallbackProviders];

    for (const providerName of providersToTry) {
      try {
        const provider = await this.getProvider(providerName);
        console.log(`[AI Factory] Using provider: ${providerName} (${provider.model})`);
        return provider;
      } catch (error) {
        console.warn(`[AI Factory] Provider ${providerName} failed:`, error.message);
        // Try next provider
      }
    }

    throw new Error(`All providers failed. Tried: ${providersToTry.join(', ')}`);
  }

  /**
   * Check if a provider is healthy
   * @param {string} providerName - Provider name
   * @param {BaseAIProvider} provider - Provider instance
   * @returns {Promise<boolean>} - True if healthy
   */
  async checkProviderHealth(providerName, provider = null) {
    const now = Date.now();
    const lastCheck = this.lastHealthCheck.get(providerName) || 0;

    // Don't check too frequently
    if ((now - lastCheck) < this.HEALTH_CHECK_INTERVAL && this.healthStatus.has(providerName)) {
      return this.healthStatus.get(providerName);
    }

    try {
      const providerInstance = provider || await this.getProvider(providerName);
      const isHealthy = await providerInstance.healthCheck();  // Fixed: was checkHealth()

      this.healthStatus.set(providerName, isHealthy);
      this.lastHealthCheck.set(providerName, now);

      if (isHealthy) {
        console.log(`[AI Factory] Provider ${providerName} is healthy`);
      } else {
        console.warn(`[AI Factory] Provider ${providerName} is not healthy`);
      }

      return isHealthy;
    } catch (error) {
      console.error(`[AI Factory] Health check failed for ${providerName}:`, error);
      this.healthStatus.set(providerName, false);
      this.lastHealthCheck.set(providerName, now);
      return false;
    }
  }

  /**
   * Get all enabled and healthy providers
   * @returns {Promise<Array>} - Array of healthy providers
   */
  async getHealthyProviders() {
    console.log('[AI Factory] Getting healthy providers...');

    const enabledProviders = await this.getEnabledProvidersFromDb();
    console.log('[AI Factory] Enabled providers from DB:', enabledProviders.length, enabledProviders.map(p => p.name));

    const healthyProviders = [];

    for (const providerConfig of enabledProviders) {
      try {
        console.log(`[AI Factory] Processing provider: ${providerConfig.name}`);
        const provider = await this.getProvider(providerConfig.name);
        console.log(`[AI Factory] Got provider instance: ${provider.model}`);

        const isHealthy = await this.checkProviderHealth(providerConfig.name, provider);
        console.log(`[AI Factory] Health check result for ${providerConfig.name}:`, isHealthy);

        if (isHealthy) {
          console.log(`[AI Factory] Adding healthy provider: ${providerConfig.name}`);
          healthyProviders.push({
            name: providerConfig.name,
            displayName: providerConfig.displayName,
            provider,
            pricing: provider.getPricing(),
            rateLimits: provider.getRateLimits()
          });
        } else {
          console.warn(`[AI Factory] Provider ${providerConfig.name} failed health check, skipping`);
        }
      } catch (error) {
        console.error(`[AI Factory] Error with provider ${providerConfig.name}:`, error);
      }
    }

    console.log(`[AI Factory] Returning ${healthyProviders.length} healthy providers`);
    return healthyProviders;
  }

  /**
   * Clear provider cache
   * Useful when configuration changes
   */
  clearCache() {
    this.providers.clear();
    this.healthStatus.clear();
    this.lastHealthCheck.clear();
    this.dbConfigs = null;
    this.lastDbLoad = 0;
  }

  /**
   * Get provider statistics
   * @returns {Object} - Provider statistics
   */
  getStatistics() {
    const stats = {
      totalProviders: this.providers.size,
      healthyProviders: Array.from(this.healthStatus.values()).filter(Boolean).length,
      unhealthyProviders: Array.from(this.healthStatus.values()).filter(v => !v).length,
      byProvider: {}
    };

    for (const [name, healthy] of this.healthStatus.entries()) {
      stats.byProvider[name] = {
        healthy,
        lastCheck: this.lastHealthCheck.get(name)
      };
    }

    return stats;
  }
}

const aiProviderFactory = new AIProviderFactory();

export default aiProviderFactory;
